import { Preferences } from "@capacitor/preferences";
import { SignUpData } from "../models/signUpData";

const PreferencesService = {
    ONBOARDING_STATUS_KEY: 'ONBOARDING_STATUS',
    PUSH_NOTIFICATION_TOKEN_KEY: 'push_notification_token',
    ACCESS_TOKEN_FIREBASE_KEY: 'access_token_firebase',
    AUTH_ID_KEY: 'auth_id',
    MUTED_KEY: 'muted',
    SESSION_ID_KEY: 'session_id',
    USER_TYPE_KEY: 'user_type',
    LOAD_SUGGESTED_TRICKS_KEY: 'load_suggested_tricks',
    SUGGESTED_TRICKS_KEY: 'suggested_tricks',
    SUGGESTED_TRICKS_TIME_LOADED_KEY: 'suggested_tricks_time_loaded',
    FEED_SETTINGS_KEY: 'feed_settings',
    COMMUNITY_TAB_KEY: 'communityTab',
    EXPLORE_TAB_KEY: 'exploreTab',
    STATS_SPORT_KEY: 'statsSport',
    STATS_PERIOD_KEY: 'statsPeriod',
    HOT_SETTINGS_KEY: 'hot_settings',
    SHOP_TAB_KEY: 'shopTab',
    RATING_PROMPT_TIME_KEY: 'rating_prompt_time',
    PUSH_NOTIFICATION_REDIRECT_URL_KEY: 'push_notification_redirect_url',
    DEEP_LINK_SLUG_KEY: 'deepLinkReferrer',
    REFERRER_KEY: 'referrer',
    INTENDED_DESTINATION_KEY: 'intended_destination',
    SIGNUP_DATA_KEY: 'signUpData',
    ONBOARDED_STATE_KEY: 'ONBOARDED_STATE',
    ITEM_SIZES_STORAGE_KEY: 'item_sizes_storage_key',
    // Story Studio: how many times we've pulsed the "Stickers" button as a
    // first-run hint. Caps the nudge so repeat sharers aren't nagged.
    STORY_STUDIO_STICKER_HINT_COUNT_KEY: 'story_studio_sticker_hint_count',
    // Transient device-global flag: a new sign-up is in progress. Set before the auth call
    // (when no uid exists yet); consumed by PhoneVerificationProvider on the first authenticated
    // pass, which binds the requirement to that uid and clears this flag so it can never leak to
    // another account on the same device.
    PHONE_VERIFICATION_PENDING_KEY: 'phone_verification_pending',
    // Durable, USER-SCOPED marker: this specific user signed up via the new flow and must verify
    // a phone. Key is `phone_verification_required:<firebase_uid>`. Legacy users never get one,
    // so they are grandfathered regardless of what other accounts did on this device.
    PHONE_VERIFICATION_REQUIRED_PREFIX: 'phone_verification_required:',

    setFirebaseToken: async (token: string) => {
        await Preferences.set({ key: PreferencesService.ACCESS_TOKEN_FIREBASE_KEY, value: token });
    },

    removeFirebaseToken: async () => {
        await Preferences.remove({ key: PreferencesService.ACCESS_TOKEN_FIREBASE_KEY });
    },

    getFirebaseToken: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.ACCESS_TOKEN_FIREBASE_KEY });
        return value;
    },

    setUserUid: async (uid: string) => {
        await Preferences.set({
            key: PreferencesService.AUTH_ID_KEY,
            value: uid,
        });
    },

    removeUserUid: async () => {
        await Preferences.remove({ key: PreferencesService.AUTH_ID_KEY });
    },

    getUserUid: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.AUTH_ID_KEY });
        return value;
    },

    // --- Story Studio sticker hint (see key comment above) ---

    getStoryStudioStickerHintCount: async (): Promise<number> => {
        const { value } = await Preferences.get({ key: PreferencesService.STORY_STUDIO_STICKER_HINT_COUNT_KEY });
        const n = parseInt(value ?? '', 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    },

    setStoryStudioStickerHintCount: async (count: number) => {
        await Preferences.set({
            key: PreferencesService.STORY_STUDIO_STICKER_HINT_COUNT_KEY,
            value: String(count),
        });
    },

    // --- Phone verification (see key comments above) ---

    // Set right before a sign-up auth call, when we don't have a uid yet.
    setPhoneVerificationPending: async () => {
        await Preferences.set({
            key: PreferencesService.PHONE_VERIFICATION_PENDING_KEY,
            value: 'true',
        });
    },

    isPhoneVerificationPending: async (): Promise<boolean> => {
        const { value } = await Preferences.get({ key: PreferencesService.PHONE_VERIFICATION_PENDING_KEY });
        return value === 'true';
    },

    clearPhoneVerificationPending: async () => {
        await Preferences.remove({ key: PreferencesService.PHONE_VERIFICATION_PENDING_KEY });
    },

    // Durable per-user "must verify a phone" marker, keyed by firebase uid.
    setPhoneVerificationRequired: async (uid: string) => {
        if (!uid) return;
        await Preferences.set({
            key: PreferencesService.PHONE_VERIFICATION_REQUIRED_PREFIX + uid,
            value: 'true',
        });
    },

    isPhoneVerificationRequired: async (uid: string): Promise<boolean> => {
        if (!uid) return false;
        const { value } = await Preferences.get({ key: PreferencesService.PHONE_VERIFICATION_REQUIRED_PREFIX + uid });
        return value === 'true';
    },

    clearPhoneVerificationRequired: async (uid: string) => {
        if (!uid) return;
        await Preferences.remove({ key: PreferencesService.PHONE_VERIFICATION_REQUIRED_PREFIX + uid });
    },

    getPushNotificationToken: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.PUSH_NOTIFICATION_TOKEN_KEY });
        return value;
    },

    setPushNotificationToken: async (token: string) => {
        await Preferences.set({
            key: PreferencesService.PUSH_NOTIFICATION_TOKEN_KEY,
            value: token
        });
    },

    setOnboardingStatus: async (status: string) => {
        await Preferences.set({
            key: PreferencesService.ONBOARDING_STATUS_KEY,
            value: status
        })
    },

    getOnboardingStatus: async () => {
        const { value } = await Preferences.get({
            key: PreferencesService.ONBOARDING_STATUS_KEY
        });
        return value;
    },

    getOnboardedState: async () => {
        const { value } = await Preferences.get({
            key: PreferencesService.ONBOARDED_STATE_KEY
        });
        return value;
    },

    setOnboardedState: async (state: string) => {
        await Preferences.set({
            key: PreferencesService.ONBOARDED_STATE_KEY,
            value: state
        });
    },

    setMutedPreferences: async (value: string) => {
        await Preferences.set({ key: PreferencesService.MUTED_KEY, value: value });
    },

    getMutedPreferences: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.MUTED_KEY })
        return value
    },

    setUserSessionId: async (sessionId: string) => {
        await Preferences.set({
            key: PreferencesService.SESSION_ID_KEY,
            value: sessionId,
        });
    },

    getUserSessionId: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.SESSION_ID_KEY });
        return value;
    },

    setUserType: async (userType: string) => {
        await Preferences.set({
            key: PreferencesService.USER_TYPE_KEY,
            value: userType,
        });
    },

    getUserType: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.USER_TYPE_KEY });
        return value;
    },

    clearPreferences: async () => {
        await Preferences.clear();
    },

    setLoadSuggestedTricks: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.LOAD_SUGGESTED_TRICKS_KEY,
            value
        });
    },

    setSuggestedTricksTimeLoaded: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.SUGGESTED_TRICKS_TIME_LOADED_KEY,
            value
        });
    },

    setSuggestedTricks: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.SUGGESTED_TRICKS_KEY,
            value
        });
    },

    getLoadSuggestedTricks: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.LOAD_SUGGESTED_TRICKS_KEY });
        return value
    },

    getSuggestedTricksKey: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.SUGGESTED_TRICKS_KEY });
        return value
    },

    getSuggestedTricksTimeLoaded: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.SUGGESTED_TRICKS_TIME_LOADED_KEY });
        return value
    },

    setFeedSettings: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.FEED_SETTINGS_KEY,
            value
        });
    },

    getFeedSettings: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.FEED_SETTINGS_KEY });
        return value
    },

    setCommunityTab: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.COMMUNITY_TAB_KEY,
            value
        });
    },

    getCommunityTab: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.COMMUNITY_TAB_KEY });
        return value
    },

    setExploreTab: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.EXPLORE_TAB_KEY,
            value
        });
    },

    getExploreTab: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.EXPLORE_TAB_KEY });
        return value
    },

    setStatsSport: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.STATS_SPORT_KEY,
            value
        });
    },

    getStatsSport: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.STATS_SPORT_KEY });
        return value;
    },

    setStatsPeriod: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.STATS_PERIOD_KEY,
            value
        });
    },

    getStatsPeriod: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.STATS_PERIOD_KEY });
        return value;
    },

    setHotSettings: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.HOT_SETTINGS_KEY,
            value
        });
    },

    getHotSettings: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.HOT_SETTINGS_KEY });
        return value
    },

    setShopTab: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.SHOP_TAB_KEY,
            value
        });
    },

    getShopTab: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.SHOP_TAB_KEY });
        return value
    },

    setRatingPromptTime: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.RATING_PROMPT_TIME_KEY,
            value
        });
    },

    getRatingPromptTime: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.RATING_PROMPT_TIME_KEY });
        return value
    },

    getPushNotificationRedirectUrl: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.PUSH_NOTIFICATION_REDIRECT_URL_KEY });
        return value;
    },

    removePushNotificationRedirectUrl: async () => {
        await Preferences.remove({ key: PreferencesService.PUSH_NOTIFICATION_REDIRECT_URL_KEY });
    },

    setPushNotificationRedirectUrl: async (url: string) => {
        await Preferences.set({
            key: PreferencesService.PUSH_NOTIFICATION_REDIRECT_URL_KEY,
            value: url
        });
    },

    setDeepLinkSlug: async (slug: string) => {
        await Preferences.set({
            key: PreferencesService.DEEP_LINK_SLUG_KEY,
            value: slug
        });
    },

    getDeepLinkSlug: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.DEEP_LINK_SLUG_KEY });
        return value;
    },

    removeDeepLinkSlug: async () => {
        await Preferences.remove({ key: PreferencesService.DEEP_LINK_SLUG_KEY });
    },

    setReferrer: async (referrer: string) => {
        await Preferences.set({
            key: PreferencesService.REFERRER_KEY,
            value: referrer
        });
    },

    getReferrer: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.REFERRER_KEY });
        return value;
    },

    removeReferrer: async () => {
        await Preferences.remove({ key: PreferencesService.REFERRER_KEY });
    },

    setIntendedDestination: async (destination: string) => {
        await Preferences.set({
            key: PreferencesService.INTENDED_DESTINATION_KEY,
            value: destination
        });
    },

    getIntendedDestination: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.INTENDED_DESTINATION_KEY });
        return value;
    },

    saveSignUpData: async (data: SignUpData) => {
        await Preferences.set({
            key: PreferencesService.SIGNUP_DATA_KEY,
            value: JSON.stringify(data)
        });
    },

    loadSignUpData: async (): Promise<SignUpData | null> => {
        const { value } = await Preferences.get({ key: PreferencesService.SIGNUP_DATA_KEY });
        return value ? JSON.parse(value) : null;
    },

    clearSignUpData: async () => {
        await Preferences.remove({ key: PreferencesService.SIGNUP_DATA_KEY });
    },

    setItemSizes: async (value: string) => {
        await Preferences.set({
            key: PreferencesService.ITEM_SIZES_STORAGE_KEY,
            value
        });
    },

    getItemSizes: async () => {
        const { value } = await Preferences.get({ key: PreferencesService.ITEM_SIZES_STORAGE_KEY });
        return value
    },
};

export default PreferencesService;